"use client";

import React, { useEffect, useRef, useState } from "react";
import { Radio, Zap, Activity } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function OurIndieMission() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const offset = (window.innerHeight - rect.top) * 0.1;
      setScrollY(offset);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222484" id="the-paradise-story" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-0 opacity-[0.03] dark:opacity-[0.05]">
        <span className="text-[15rem] md:text-[25rem] lg:text-[35rem] font-black font-default tracking-tighter whitespace-nowrap">
          KSANTI
        </span>
      </div>

      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/10 blur-[120px] rounded-lg mix-blend-screen" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent/10 blur-[120px] rounded-lg mix-blend-screen" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div className="col-span-1 lg:col-span-5 flex flex-col h-full">
            <div className="mb-8 flex items-center gap-3">
              <Activity className="size-6 text-primary animate-pulse" />
              <span className="font-mono text-sm tracking-widest text-primary uppercase">
                Est. Saranda 2024
              </span>
            </div>
            
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold font-serif uppercase tracking-tighter leading-[0.9] mb-8">
              The <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary-foreground">
                Paradise
              </span> <br />
              Story
            </h2>

            <ScrollArea className="h-[400px] lg:h-[550px] pr-6 border-l-2 border-primary/20 pl-6">
              <div className="space-y-8 pb-8">
                <p className="text-xl md:text-2xl font-medium leading-relaxed font-default text-foreground/90">
                  Frozen Paradise by Ksanti is a love letter to the Ionian coast, blending artisanal sweets with Mediterranean soul.
                </p>
                
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-serif italic">
                  Born in the heart of Saranda, our cafe was founded on the heritage of Albanian hospitality. We believe that every scoop of gelato and every hand-crafted cocktail should carry the warmth of the sun and the freshness of the sea breeze.
                </p>

                <div className="p-6 bg-accent/50 border border-border relative overflow-hidden group rounded-lg shadow-md">
                  <div className="absolute inset-0 bg-primary/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                  <Radio className="size-8 text-primary mb-4 relative z-10" />
                  <p className="text-sm font-mono text-foreground/80 relative z-10">
                    From morning espresso rituals to late-night signature cocktails, we curate a sensory experience that celebrates the vibrant spirit of Ksamil and Saranda.
                  </p>
                </div>

                <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-serif">
                  Our commitment to quality means sourcing the finest local ingredients for our pastries and ice cream. Whether you are here for a quiet coffee or a celebratory drink, you are part of the Ksanti family. This is where the Riviera meets pure indulgence.
                </p>

                <div className="pt-4">
                  <Button asChild size="lg" className="rounded-lg font-default uppercase tracking-wider group relative overflow-hidden bg-primary hover:bg-primary/90 shadow-lg">
                    <Link to="/OurMenuPage">
                      <span className="relative z-10 flex items-center gap-2">
                        <Zap className="size-4 group-hover:animate-bounce" />
                        Explore the Menu
                      </span>
                    </Link>
                  </Button>
                </div>
                
                <div className="pt-6">
                  <span className="text-3xl font-serif italic text-primary opacity-80">Ksanti</span>
                </div>
              </div>
            </ScrollArea>
          </div>

          {}
          <div className="col-span-1 lg:col-span-7 relative h-[600px] lg:h-[800px] w-full hidden md:block">
            
            {}
            <div className="absolute top-[5%] right-[5%] w-[60%] h-[65%] z-10 transition-transform duration-100 ease-out will-change-transform" style={{
            transform: `translateY(${scrollY * 0.3}px)`
          }}>
              <div className="w-full h-full relative overflow-hidden border border-border/50 bg-secondary/20 rounded-lg shadow-xl">
                <div className="absolute inset-0 bg-background/20 mix-blend-overlay z-10" />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/saranda_cafe_interior.png" alt="Frozen Paradise Interior" className="w-full h-full object-cover opacity-90" />
              </div>
            </div>

            {}
            <div className="absolute top-[25%] left-[0%] w-[55%] h-[50%] z-20 transition-transform duration-100 ease-out will-change-transform" style={{
            transform: `translateY(${scrollY * 0.6}px)`
          }}>
              <div className="w-full h-full relative overflow-hidden border-4 border-background shadow-2xl rounded-lg">
                <div className="absolute inset-0 bg-primary/10 mix-blend-color z-10" />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/artisanal_pastry_display.png" alt="Handmade Pastries" className="w-full h-full object-cover saturate-100" />
              </div>
            </div>

            {}
            <div className="absolute bottom-[10%] right-[15%] w-[45%] h-[45%] z-30 transition-transform duration-100 ease-out will-change-transform" style={{
            transform: `translateY(${scrollY * 1.1}px)`
          }}>
              <div className="w-full h-full relative overflow-hidden border-2 border-primary shadow-xl rounded-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/signature_cocktail_saranda.png" alt="Summer Cocktail" className="w-full h-full object-cover" />
                {}
                <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz4KPC9zdmc+')] pointer-events-none" />
              </div>
            </div>

          </div>
          
          {}
          <div className="col-span-1 md:hidden flex flex-col gap-4 mt-8">
             <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/saranda_cafe_interior.png" alt="Frozen Paradise Interior" className="w-full h-64 object-cover border border-border rounded-lg shadow-md" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/artisanal_pastry_display.png" alt="Handmade Pastries" className="w-full h-64 object-cover border border-border rounded-lg shadow-md" />
          </div>

        </div>
      </div>
    </section>;
}